import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetch_month_run,
  fetch_month_runs,
  post_month_run_cancel,
  post_month_run_resume
} from '../../api';
import { run_request_failure, run_view } from './adapters';
import {
  BUCKET_NEEDS_ATTENTION,
  MAX_RUN_ITEMS_PAGE,
  RUN_ITEMS_PAGE,
  is_run_active,
  poll_delay_ms,
  run_cancel_payload,
  run_resume_payload
} from './params';

/*
 * the month's run for the Needs attention tab. On mount (and on a month change) it reads GET
 * /month/runs and picks up the ACTIVE run, so a reload or a closed tab resumes the panel.
 */
const is_hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

const useMonthRun = ({ pay_month, on_finished }) => {
  const [data, setData] = useState(null);
  const [bucket, setBucketState] = useState('ALL');
  const [offset, setOffset] = useState(0);
  // 'runs': the month's runs could not be read; 'run': the run itself could not be read.
  const [load_failed, setLoadFailed] = useState('');
  const [poll_failed, setPollFailed] = useState(false);
  const [items_loading, setItemsLoading] = useState(false);
  const [action_busy, setActionBusy] = useState('');
  const [action_error, setActionError] = useState('');
  // Bumped when a run is adopted, so the panel moves focus to itself.
  const [focus_token, setFocusToken] = useState(0);
  // Each answered poll bumps it; the effect below schedules the next one from it (no overlap).
  const [tick, setTick] = useState(0);
  // The focus token the panel already acted on; kept here so a remounted panel does not refocus.
  const focused_ref = useRef(0);

  const uuid_ref = useRef('');
  const seq = useRef(0);
  const in_flight = useRef(false);
  const was_active = useRef(false);
  const bucket_touched = useRef(false);
  const view_ref = useRef({ bucket, offset });
  view_ref.current = { bucket, offset };
  const finished_ref = useRef(on_finished);
  finished_ref.current = on_finished;

  // load_run is defined below and reads land; land re-reads through this ref.
  const load_ref = useRef(null);

  const land = useCallback((run) => {
    setData(run);
    const active = is_run_active(run && run.status);
    const finished = was_active.current && !active;
    was_active.current = active;
    if (!finished) return;
    if (finished_ref.current) finished_ref.current(run_view(run));
    // A finish with teachers to look at opens on them, unless Ops already picked a list.
    if (!bucket_touched.current && run.counts && Number(run.counts.needs_attention) > 0) {
      view_ref.current = { bucket: BUCKET_NEEDS_ATTENTION, offset: 0 };
      setBucketState(BUCKET_NEEDS_ATTENTION);
      setOffset(0);
      if (load_ref.current) load_ref.current(true);
    }
  }, []);

  // One read of the run's current page; stale answers (month or bucket moved on) are dropped.
  const load_run = useCallback(
    (manual) => {
      const run_uuid = uuid_ref.current;
      if (!run_uuid) return Promise.resolve();
      seq.current += 1;
      const s = seq.current;
      const { bucket: b, offset: o } = view_ref.current;
      in_flight.current = true;
      if (manual) setItemsLoading(true);
      return fetch_month_run(run_uuid, b, RUN_ITEMS_PAGE, o)
        .then((run) => {
          if (s !== seq.current) return;
          land(run);
          setLoadFailed('');
          setPollFailed(false);
        })
        .catch(() => {
          if (s !== seq.current) return;
          if (manual) setLoadFailed('run');
          else setPollFailed(true);
        })
        .finally(() => {
          if (s !== seq.current) return;
          in_flight.current = false;
          setItemsLoading(false);
          setTick((t) => t + 1);
        });
    },
    [land]
  );
  load_ref.current = load_run;

  const load_active = useCallback(() => {
    seq.current += 1;
    const s = seq.current;
    uuid_ref.current = '';
    was_active.current = false;
    setData(null);
    setLoadFailed('');
    setPollFailed(false);
    setActionError('');
    if (!pay_month) return;
    fetch_month_runs(pay_month)
      .then((runs) => {
        if (s !== seq.current) return;
        const active = runs && runs.active;
        if (!active || !active.run_uuid) return;
        uuid_ref.current = active.run_uuid;
        was_active.current = true;
        bucket_touched.current = false;
        setBucketState('ALL');
        setOffset(0);
        view_ref.current = { bucket: 'ALL', offset: 0 };
        load_run(true);
      })
      .catch(() => {
        if (s !== seq.current) return;
        setLoadFailed('runs');
      });
  }, [pay_month, load_run]);

  useEffect(() => {
    load_active();
    return () => {
      // Unmount or month change: no answer lands, no poll is scheduled.
      seq.current += 1;
    };
  }, [load_active]);

  const active = is_run_active(data && data.status);

  useEffect(() => {
    if (!active || !uuid_ref.current) return undefined;
    const timer = setTimeout(
      () => {
        if (!in_flight.current) load_run(false);
      },
      poll_delay_ms(is_hidden(), data && data.status)
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tick, load_run]);

  // Coming back to the tab re-schedules at the visible rate instead of waiting out a hidden delay.
  useEffect(() => {
    if (!active) return undefined;
    const on_visibility = () => setTick((t) => t + 1);
    document.addEventListener('visibilitychange', on_visibility);
    return () => document.removeEventListener('visibilitychange', on_visibility);
  }, [active]);

  // A run the dialog just started (run may be null: the create went unanswered and GET found it).
  const adopt = useCallback(
    (run_uuid, run) => {
      seq.current += 1;
      uuid_ref.current = run_uuid;
      was_active.current = true;
      bucket_touched.current = false;
      setBucketState('ALL');
      setOffset(0);
      view_ref.current = { bucket: 'ALL', offset: 0 };
      setLoadFailed('');
      setActionError('');
      setFocusToken((t) => t + 1);
      in_flight.current = false;
      if (run) land(run);
      else load_run(true);
      setTick((t) => t + 1);
    },
    [land, load_run]
  );

  const set_bucket = (b) => {
    bucket_touched.current = true;
    view_ref.current = { bucket: b, offset: 0 };
    setBucketState(b);
    setOffset(0);
    load_run(true);
  };

  const set_page = (o) => {
    view_ref.current = { ...view_ref.current, offset: o };
    setOffset(o);
    load_run(true);
  };

  // Cancel and resume answer the run with the default page; the panel's own page is re-read after.
  const act = (name, request) => {
    setActionBusy(name);
    setActionError('');
    return request
      .then(() => {
        setActionBusy('');
        return load_run(true).then(() => true);
      })
      .catch((e) => {
        setActionBusy('');
        const failure = run_request_failure(e);
        setActionError(
          failure.message ||
            (name === 'cancel' ? 'The run could not be stopped.' : 'The run could not be resumed.')
        );
        load_run(true);
        return false;
      });
  };

  const cancel = (reason) =>
    act('cancel', post_month_run_cancel(run_cancel_payload(uuid_ref.current, reason)));

  // expected_takeover: what the button said; a stale page is refused and the run re-read.
  const resume = (expected_takeover) =>
    act('resume', post_month_run_resume(run_resume_payload(uuid_ref.current, expected_takeover)));

  // Every item of one bucket, page by page (a run holds at most 2,000), for "Retry the teachers that failed".
  const load_all_items = async (b) => {
    const run_uuid = uuid_ref.current;
    const items = [];
    let total = 1;
    while (items.length < total) {
      // eslint-disable-next-line no-await-in-loop
      const page = await fetch_month_run(run_uuid, b, MAX_RUN_ITEMS_PAGE, items.length);
      const rows = Array.isArray(page.items) ? page.items : [];
      total = Number(page.items_total) || 0;
      items.push(...rows);
      if (!rows.length) break;
    }
    return items;
  };

  // A terminal run's panel can be put away; an active one cannot.
  const dismiss = () => {
    if (active) return;
    seq.current += 1;
    uuid_ref.current = '';
    setData(null);
    setActionError('');
  };

  // A failed read of a known run re-reads that run; otherwise the month's runs are read again.
  const reload = () => {
    if (uuid_ref.current) load_run(true);
    else load_active();
  };

  return {
    run: data ? run_view(data) : null,
    raw_run: data,
    active,
    bucket,
    offset,
    load_failed,
    poll_failed,
    items_loading,
    action_busy,
    action_error,
    focus_token,
    focused_token: focused_ref.current,
    on_focused: (token) => {
      focused_ref.current = token;
    },
    adopt,
    set_bucket,
    set_page,
    cancel,
    resume,
    load_all_items,
    dismiss,
    reload
  };
};

export default useMonthRun;
